// Global Instructions Rule Applied!
// Fund-accounting cfg lookups delivered via get_global_configs_v3:
// cfg.time_units, cfg.reference_date_types, cfg.gp_commitment_bases,
// cfg.fee_bases, cfg.compounding_frequencies, cfg.day_count_conventions,
// cfg.fund_transaction_types, cfg.acc_items.
import { z } from 'zod'

export const TimeUnitModelSchema = z
  .object({
    id: z.number(),
    label: z.string(),
    months_per_unit: z.number(),
    display_order: z.number().nullish()
  })
  .strict()

export const ReferenceDateTypeModelSchema = z
  .object({
    id: z.number(),
    label: z.string(),
    display_order: z.number().nullish()
  })
  .strict()

export const GpCommitmentBasisModelSchema = z
  .object({
    id: z.number(),
    label: z.string(),
    display_order: z.number().nullish()
  })
  .strict()

export const FeeBasisModelSchema = z
  .object({
    id: z.number(),
    label: z.string(),
    display_order: z.number().nullish(),
    // Net Invested Capital rows only: 'book' = cost held as on the ledger,
    // 'cash' = cost held less capitalised interest and non-cash basis
    // (cfg.fee_bases.basis_projection, 2026-10-21); null on every other basis
    basis_projection: z.string().nullish()
  })
  .strict()

export const LateCloserPolicyModelSchema = z
  .object({
    id: z.number(),
    label: z.string(),
    display_order: z.number().nullish()
  })
  .strict()

// cfg.fee_break_treatments (spec A3.9): how capital calls treat an LP's
// side-letter fee break - contribute less (default) or contribute in full
export const FeeBreakTreatmentModelSchema = z
  .object({
    id: z.number(),
    label: z.string(),
    contributes_in_full: z.boolean(),
    description: z.string().nullish(),
    display_order: z.number().nullish()
  })
  .strict()

// cfg.fee_period_switch_timings: when the next fee period's rate and basis
// take over - on the switch date (default) or deferred to the start of the
// next billing period
export const FeePeriodSwitchTimingModelSchema = z
  .object({
    id: z.number(),
    label: z.string(),
    defers_to_billing_period_start: z.boolean(),
    description: z.string().nullish(),
    display_order: z.number().nullish()
  })
  .strict()

// cfg.waived_fee_settlements (spec A3.9 addendum part 1): how a fund settles
// its reduced-fee partners' waived fees - None (nothing owed), Returnable
// (owed; a GP-discretionary return of contributions) or Special distribution
// (owed; repaid ahead of ordinary distributions). Behaviour reads the flags,
// never the labels.
export const WaivedFeeSettlementModelSchema = z
  .object({
    id: z.number(),
    label: z.string(),
    owed: z.boolean(),
    priority_distribution: z.boolean(),
    description: z.string().nullish(),
    display_order: z.number().nullish()
  })
  .strict()

export const CatchupKindModelSchema = z
  .object({
    id: z.number(),
    label: z.string()
  })
  .strict()

export const CompoundingFrequencyModelSchema = z
  .object({
    id: z.number(),
    label: z.string(),
    /** months per period (12 / 6 / 3 / 1; 0 = daily); null on a once-off row */
    value: z.number().nullish(),
    /** the management fee is charged once, in full, on each commitment's charge date */
    once_off: z.boolean().optional().default(false)
  })
  .strict()

export const DayCountConventionModelSchema = z
  .object({
    id: z.number(),
    label: z.string()
  })
  .strict()

export const FundTransactionTypeModelSchema = z
  .object({
    id: z.number(),
    label: z.string(),
    debit_item_id: z.string().nullish(),
    credit_item_id: z.string().nullish(),
    journals_separate_tab: z.boolean(),
    display_order: z.number().nullish(),
    orchestrated: z.boolean(),
    books_fee_period: z.boolean().nullish(),
    // booking closes the linked commitments instead of posting journals
    closes_commitments: z.boolean().nullish(),
    // booking re-points the linked commitment to the transferee (no journals)
    transfers_interest: z.boolean().nullish()
  })
  .strict()

export const FeeReductionSchemeModelSchema = z
  .object({
    id: z.number(),
    label: z.string()
  })
  .strict()

export const AccItemModelSchema = z
  .object({
    id: z.string(),
    label: z.string(),
    call_component: z.boolean()
  })
  .strict()

export type TimeUnitModel = z.infer<typeof TimeUnitModelSchema>
export type ReferenceDateTypeModel = z.infer<typeof ReferenceDateTypeModelSchema>
export type GpCommitmentBasisModel = z.infer<typeof GpCommitmentBasisModelSchema>
export type FeeBasisModel = z.infer<typeof FeeBasisModelSchema>
export type CompoundingFrequencyModel = z.infer<typeof CompoundingFrequencyModelSchema>
export type DayCountConventionModel = z.infer<typeof DayCountConventionModelSchema>
export type FundTransactionTypeModel = z.infer<typeof FundTransactionTypeModelSchema>
export type AccItemModel = z.infer<typeof AccItemModelSchema>
export type FeeReductionSchemeModel = z.infer<typeof FeeReductionSchemeModelSchema>
